import React from 'react';
import { t } from '@lingui/core/macro';

import CalloutBadge from '@rover/components/src/callouts/CalloutBadge/CalloutBadge';
import Boarding from '@rover/icons/svg/boarding.svg';
import Daycare from '@rover/icons/svg/daycare.svg';
import DropIn from '@rover/icons/svg/drop-in.svg';
import Grooming from '@rover/icons/svg/grooming.svg';
import Sitting from '@rover/icons/svg/sitting.svg';
import Walking from '@rover/icons/svg/walking.svg';
import { Box, Flex, Text } from '@rover/kibble/core';
import IconBlock from '@rover/kibble/patterns/IconBlock/IconBlock';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import type { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';
import type { EnhancedPersonSitterProfileServiceAddOn } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileServiceAddOn';
import useIsBHUser from '@rover/shared/js/hooks/useIsBHUser';
import {
  coercePriceTransparencyLevel,
  getIsPriceTransparencyFull,
} from '@rover/utilities/priceTransparency';

import { formatPrice } from './utils';

type Props = {
  service: EnhancedPersonSitterProfileService;
  standardRate?: EnhancedPersonSitterProfileServiceAddOn;
  textSize?: '100' | '200' | '300';
  priceInCredits?: string | null;
  showSittersRates?: boolean;
};

const serviceIconToGraphic = {
  'sitter-home': Boarding,
  'sitter-traveling': Sitting,
  homevists: DropIn,
  daycare: Daycare,
  walking: Walking,
  grooming: Grooming,
};

const ServiceHeader = ({
  service,
  standardRate,
  priceInCredits,
  showSittersRates,
  textSize = '300',
}: Props): JSX.Element => {
  const { locale } = useI18nContext();
  const isBHUser = useIsBHUser();
  const { displayName, icon, description, isAway } = service;
  const isPtFull = getIsPriceTransparencyFull(
    coercePriceTransparencyLevel(service.priceTransparencyLevel)
  );
  return (
    <Flex width="100%" justifyContent="space-between" data-testid="service-detail-header">
      <Box pr="3x">
        {serviceIconToGraphic[icon] && (
          <IconBlock icon={serviceIconToGraphic[icon]} title={displayName} size={textSize}>
            <Text size="100" textColor="tertiary">
              {description}
            </Text>
          </IconBlock>
        )}
      </Box>
      <Flex mt="1x">
        {isAway && (
          <CalloutBadge
            data-testid="away-badge"
            value={t`AWAY`}
            calloutBadgeStyle={CalloutBadge.Styles.NEW_AWAY}
          />
        )}
        {!isAway && standardRate && (
          <Flex flexDirection="column" alignItems="end" textAlign="right">
            <Text data-testid="rate" fontWeight="semibold" size={textSize}>
              {formatPrice(
                standardRate,
                locale,
                isPtFull || isBHUser,
                !showSittersRates && isBHUser ? priceInCredits : null
              )}
            </Text>
            <Text size="100" textColor="secondary">
              {standardRate.unitAsSentence}
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default ServiceHeader;
